import {Component, EventEmitter, inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import { StepsModule } from 'primeng/steps';
import { MenuItem, MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { IGetHojaDeVidaJoinInfo, ITipoTramite, TipoTramiteDto } from '../../../shared/interfaces/database/configuracion';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { AdminTipoTramiteActividadComponent } from '../admin-tramites/admin-tipo-tramite-actividad/admin-tipo-tramite-actividad.component';
import { Router } from '@angular/router';
import { TipoTramiteCostoComponent } from '../admin-tramites/tipo-tramite-costo/tipo-tramite-costo.component';
import { ImpuestoConceptoService } from '../../../services/database/impuesto-concepto.service';
import { WizardTipoTramiteFormComponent } from './wizard-tipo-tramite-form/wizard-tipo-tramite-form.component';
import { LoadingPanelService } from 'lib-base';
import { TipoTramiteService } from '../../../services/database/tipoTramite.service';
import {CustomTitleDirective} from "../../../shared/clases/custom-title.directive";

type TabKey = 'INFORMACION_GENERAL' | 'TRAMITE_COSTO' | 'ACTIVIDADES' | 'FINAL';

@Component({
  selector: 'app-tramite-wizard',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, WizardTipoTramiteFormComponent,
    StepsModule, CardModule, ButtonModule, DropdownModule, MultiSelectModule,
    AdminTipoTramiteActividadComponent, TipoTramiteCostoComponent
  ],
  templateUrl: './tramite-wizard.component.html',
  styleUrls: ['./tramite-wizard.component.scss', '../../../../assets/styles/style.mantenimiento.scss']
})
export class TramiteWizardComponent implements OnInit {
  @Input() editMode: boolean = false;
  @Output() onFinalizado: EventEmitter<void> = new EventEmitter<void>();
  private titleService:CustomTitleDirective = inject(CustomTitleDirective);

  items: MenuItem[] = [];
  isLoading = false;
  activeIndex: number = 0;
  showNextPageButton: boolean = false;
  //esto es para controlar el flujo de la pagina, si el usuario presiona el boton siguiente entonces is true
  private _nextButtonPressed: boolean = false;

  translations: any = {};
  tabs: Record<TabKey, number> = {
    INFORMACION_GENERAL: 0,
    TRAMITE_COSTO: 1,
    ACTIVIDADES: 2,
    FINAL: 3
  };

  //estas propiedades se usan para guardar la informacion del tramite
  private _tipoTramiteForm !: TipoTramiteDto;
  savedTipoTramiteId !: number;


  @ViewChild(WizardTipoTramiteFormComponent) wizardTipoTramiteFormComponent !: WizardTipoTramiteFormComponent;
  @ViewChild(AdminTipoTramiteActividadComponent) tipoTramiteActividadComponent !: AdminTipoTramiteActividadComponent;
  @ViewChild(TipoTramiteCostoComponent) tipoTramiteImpuesto !: TipoTramiteCostoComponent;

  constructor(private readonly _translate: TranslateService, private readonly _ms: MessageService, private readonly _router: Router, private readonly _ics: ImpuestoConceptoService,
    private readonly _lps: LoadingPanelService, private readonly _tt: TipoTramiteService
  ) {
  }

  get isFirstPage() {
    return this.activeIndex == 0;
  }

  get isLastPage() {
    return this.activeIndex == this.items.length - 1;
  }

  ngOnInit() {
    this.titleService.changeTitle(this.editMode ? 'msg.editarHojaDeVida' : 'app-menu.tramite-wizard');
    this.translate();
    this.setUpTabPage();
  }

  private async translate(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      dependencia: this._translate.get('components.app-tramite-wizard.tabs.dependencia'),
      categorizacion: this._translate.get('components.app-tramite-wizard.tabs.categorizacion'),
      formulario: this._translate.get('components.app-tramite-wizard.tabs.formulario'),
      tramite: this._translate.get('components.app-tramite-wizard.tabs.tramite'),
      actividades: this._translate.get('components.app-tramite-wizard.tabs.actividades'),
      final: this._translate.get('components.app-tramite-wizard.tabs.final'),
      errorImportando: this._translate.get('components.app-tramite-wizard.tabs.errorImportando'),
      completeCampos: this._translate.get('form.msg.complete-campos'),
      warnSummary: this._translate.get('form.msg.warn-summary'),
      errorSummary: this._translate.get('form.msg.error-summary'),
      impuesto: this._translate.get('common.tramiteCosto'),
      informacionGeneralTramite: this._translate.get('msg.informacionGeneralTramite'),
    }));

    this.configuraTabNames();
  }

  configuraTabNames() {
    this.items = [
      { label: this.translations.informacionGeneralTramite },
      { label: this.translations.impuesto },
      { label: this.translations.actividades },
      { label: this.translations.final }
    ];
  }

  loadWizard(tramite: ITipoTramite, informacionExtra: IGetHojaDeVidaJoinInfo) {
    this.savedTipoTramiteId = tramite.id;

    this._tipoTramiteForm = {
      categorizacionId: tramite.categorizacionId,
      formularioId: tramite.formularioId,
      formularioVersion: tramite.formularioVersion,
      //control de tiempo
      controlDeTiempoId: tramite.controlDeTiempoId,
      controlDeTiempoVersion: tramite.controlDeTiempoVersion,
      
      descripcion: tramite.descripcion,
      descripcionBreve: tramite.descripcionBreve,
      produccion: false,
      dependenciaId: tramite.dependenciaId,
      canalAtencionIds: informacionExtra?.canalesAtencionIds,
      enfoqueDiferencialIds: informacionExtra?.enfoqueDiferencialIds,
      caracteristicaEspecialIds: informacionExtra?.caracteristicaEspecialIds,
      dirigidoTramiteIds: informacionExtra?.dirigidoTramiteIds,
      periodo: tramite.periodo,
      horariosAtencion: tramite.horariosAtencion,
      requisitos: tramite.requisitos,
      ruta: tramite.ruta,
      relacion: tramite.relacion,
      norma: tramite.norma,

      requierePago: tramite.requierePago,
      tipoCuenta: tramite.requierePago ? tramite.tipoCuenta : ' ',
      nombreBanco: tramite.requierePago ? tramite.nombreBanco : ' ',
      numeroCuenta: tramite.requierePago ? tramite.numeroCuenta : ' ',
      conceptosTramitesId: tramite.conceptosTramitesId,
      iac: tramite.iac,

      direccion: tramite.direccion,
      telefono: tramite.telefono,
      correo: tramite.correo,
      observaciones: tramite.observaciones,
      urlTramite: tramite.urlTramite,
      asignacionManual: tramite.asignacionManual,
      tieneFirmaComprobante: tramite.tieneFirmaComprobante,
      nombreFirmante: tramite.nombreFirmante,
      //Correos plantilla
      respuestaDinamicaPlantillaId: tramite.respuestaDinamicaPlantillaId,
      respuestaDinamicaPlantillaVersion: tramite.respuestaDinamicaPlantillaVersion,
      respuestaDinamicaPlantillaIsLatest: tramite.respuestaDinamicaPlantillaIsLatest,
      //componente personalizado
      customComponentId: tramite.componentesPersonalizadosId,
      customComponentVersion: tramite.componentesPersonalizadosVersion
    } as TipoTramiteDto;

    if (typeof tramite.respuestaDinamicaConfiguracionLlaves === 'string') {
      try {
        this._tipoTramiteForm.respuestaDinamicaConfiguracionLlaves = JSON.parse(tramite.respuestaDinamicaConfiguracionLlaves);
      } catch {
        this._tipoTramiteForm.respuestaDinamicaConfiguracionLlaves = [];
      }
    }
    this.setUpTabPage();
  }

  //#region Tab control
  //controla el flujo de la paginacion
  async nextPage() {
    this._nextButtonPressed = true;
    this._lps.show(true);
    if (!this.canOpenNextPage()) {
      this._lps.hide();
      this._ms.add({ severity: 'warn', summary: this.translations.warnSummary, detail: this.translations.completeCampos });
      return;
    }

    // Wait for processPage to complete before continuing
    const result = await this.processPage();

    // Only proceed if processing was successful
    if (result) {
      this.activeIndex++;
      this.setUpTabPage();
    }
    this._lps.hide();
  }

  //controla el flujo de la paginacion
  prevPage() {
    this._nextButtonPressed = false;
    if (this.isFirstPage) return;
    this.activeIndex--;
    this.setUpTabPage();
  }

  private canOpenNextPage(): boolean {
    switch (this.activeIndex) {
      case this.tabs.INFORMACION_GENERAL:
        return this.wizardTipoTramiteFormComponent.isFormCompleted();
      case this.tabs.TRAMITE_COSTO:
        {
          if (this._tipoTramiteForm && !this._tipoTramiteForm.requierePago) {
            return true;
          }
          if (this._tipoTramiteForm.conceptosTramitesId) {
            return true;
          } else {
            return this.tipoTramiteImpuesto.tipoTramiteCosto.length > 0;
          }
        }
      case this.tabs.ACTIVIDADES:
        return this.tipoTramiteActividadComponent.dataset.length > 0;
      default:
        return false;
    }
  }

  private processPage(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      switch (this.activeIndex) {
        case this.tabs.INFORMACION_GENERAL:
          const tieneFirmaComprobanteOg = this._tipoTramiteForm?.tieneFirmaComprobante || false;
          this._tipoTramiteForm = this.wizardTipoTramiteFormComponent.getForm();
          const firmaDeComprobante = this.wizardTipoTramiteFormComponent.firmaDeComprobante;
          const firmaCambio = tieneFirmaComprobanteOg !== this._tipoTramiteForm?.tieneFirmaComprobante;
          if (this.savedTipoTramiteId) {
            if (this.wizardTipoTramiteFormComponent.isPristine && !firmaCambio) {
              resolve(true);
              return;
            }
            this._tt.updateTipoTramite(this.savedTipoTramiteId, this._tipoTramiteForm, firmaDeComprobante).subscribe({
              next: (response: string) => {
                this._ms.add({ severity: 'success', summary: 'Éxito', detail: 'Hoja de vida actualizado exitosamente.' });
                resolve(true);
              },
              error: (err) => {
                resolve(false);
              }
            });
          } else {
            this._tt.createTipoTramite(this._tipoTramiteForm, firmaDeComprobante).subscribe({
              next: (id: number) => {
                this._ms.add({ severity: 'success', summary: 'Éxito', detail: 'Hoja de vida creado exitosamente.' });
                this.savedTipoTramiteId = id;
                resolve(true);
              },
              error: (err) => {
                resolve(false);
              }
            });
          }
          break;
        case this.tabs.TRAMITE_COSTO:
          if (this._tipoTramiteForm?.requierePago) {
            if (this._tipoTramiteForm?.conceptosTramitesId) {
              resolve(true);
            } else if (this.tipoTramiteImpuesto.tipoTramiteCosto.length > 0) {
              resolve(true);
            } else {
              resolve(false);
            }
          } else {
            resolve(true);
          }
          break;
        case this.tabs.ACTIVIDADES:
          resolve(true);
          break;
        default:
          resolve(false);
      }
    });
  }

  private setUpTabPage() {
    this.showNextPageButton = true;
    switch (this.activeIndex) {
      case this.tabs.INFORMACION_GENERAL:
        if (this.savedTipoTramiteId && this._tipoTramiteForm) {
          setTimeout(() => {
            this.wizardTipoTramiteFormComponent.loadForm(this._tipoTramiteForm);
          }, 0);
        }
        break;
      case this.tabs.TRAMITE_COSTO:
        if (this._tipoTramiteForm?.requierePago) {
          if (this._tipoTramiteForm?.conceptosTramitesId) {
            if (this._nextButtonPressed) {
              this.nextPage();
            } else {
              this.prevPage();
            }
          }
        } else if (this._nextButtonPressed) {
          this.nextPage();
        } else {
          this.prevPage();
        }
        break;
      case this.tabs.ACTIVIDADES:
        break;
      case this.tabs.FINAL:
        this.showNextPageButton = false;
        break;
      default:
        return;
    }
  }
  //#endregion

  openLink(index: number) {
    switch (index) {
      case 0:
        window.open(`/#/configuracion/admin-tramites?tabIndex=1`, '_blank');
        break;
      case 1:
        this._router.navigate(['/configuracion/admin-tramites'], { queryParams: { tabIndex: 0 } });
        break;
    }
  }

  getHeader() {
    if (this.items.length == 0) return;
    return this.items[this.activeIndex].label;
  }

  Finish() {
    if (this.editMode) {
      this.onFinalizado?.emit();
    } else {
      this.openLink(1);
    }
  }
}
